<app-pan-modal size="fs" #imageModal>
  @let selectedCameraData = selectedCamera();

  <div header>
    {{ selectedCameraData?.cameraName || "Camera Snapshot" }}
  </div>

  <div body class="h-full w-full !p-0">
    @if (selectedCameraData) {
      <img
        [alt]="selectedCameraData.cameraName"
        class="h-full w-full select-none rounded-lg object-cover"
        [src]="selectedCameraData.blob | blobToImage"
      />
    }
  </div>

  <div footer>
    <app-pan-button panModalClose>Close</app-pan-button>
  </div>
</app-pan-modal>

<app-pan-modal size="md" #requestNowModal>
  <div header>Request Photo Preview</div>

  <div body class="flex flex-col gap-3">
    @if (photoPreviewMode() === "selected") {
      <app-pan-form-control>
        <app-pan-floating-label>Select time</app-pan-floating-label>
        <input
          panInput
          type="time"
          [(ngModel)]="selectedSnapshotTime"
          (change)="loadSnapshotsForTime(selectedSnapshotTime())"
          placeholder="00:00:00"
        />
      </app-pan-form-control>
    }

    @if (previewCameras().length > 0) {
      <app-pan-form-control>
        <app-pan-floating-label>Select camera</app-pan-floating-label>
        <ng-select
          [items]="previewCameras()"
          bindLabel="cameraName"
          bindValue="locationCameraId"
          [clearable]="false"
          [ngModel]="activePreviewCameraId()"
          (ngModelChange)="setActivePreviewCamera($event)"
        ></ng-select>
      </app-pan-form-control>

      <div class="h-[300px]">
        @let activeCam = activePreviewCam();
        @if (activeCam) {
          @if (activeCam.isLoading) {
            <app-pan-blank-slate>
              <app-pan-spinner size="lg"></app-pan-spinner>
            </app-pan-blank-slate>
          } @else if (activeCam.error) {
            <app-pan-inline-alert [type]="'danger'" class="w-full">Could not load preview for this camera.</app-pan-inline-alert>
          } @else if (activeCam.image) {
            <img
              [alt]="activeCam.cameraName"
              class="w-full h-full object-cover rounded"
              [src]="'data:image/jpeg;base64,' + activeCam.image"
            />
          } @else {
            <app-pan-blank-slate>
              <mat-icon class="scale-[2]">videocam_off</mat-icon>
            </app-pan-blank-slate>
          }
        }
      </div>

      <div class="text-sm font-semibold mt-2">Do you want to store these requested photos?</div>
    }
  </div>

  <div footer class="flex justify-end gap-3 items-center w-full">
    <app-pan-button color="info" fill="outline" panModalClose [disabled]="isStoringPhotos()">No, don't store it!</app-pan-button>
    <app-pan-button
      color="info"
      (click)="saveSnapshotRequest()"
      [disabled]="isStorePhotosDisabled()"
      [state]="isStoringPhotos() ? 'loading' : 'default'"
      >Yes, store it!</app-pan-button
    >
  </div>
</app-pan-modal>

@if (hasCameraMissingRegionOfInterest()) {
  <app-pan-inline-alert type="danger" class="mb-4" data-testid="snapshot-detail-missing-region-of-interest">
    This market does not have a region of interest. To label your market, please contact
    <a class="font-bold underline" href="mailto:support@panoptyc.com">support&#64;panoptyc.com</a>
  </app-pan-inline-alert>
}

<app-pan-card class="mb-4">
  <div class="p-5">
    <div class="flex items-baseline gap-3 mb-4">
      <h2 class="text-2xl font-bold" data-testid="snapshot-detail-location-name">{{ locationName() }}</h2>
      @if (branchName()) {
        <a
          class="text-primary text-lg font-medium hover:underline"
          [routerLink]="['/home', 'manage', 'branch', branchId()]"
          data-testid="snapshot-detail-branch-link"
        >
          {{ branchName() }}
        </a>
      }
    </div>

    <div class="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
      <div class="space-y-1">
        @for (camera of locationCameras(); track camera.location_camera_id; let i = $index) {
          <div class="flex gap-2">
            <span class="whitespace-nowrap font-semibold">Camera Name {{ i + 1 }}:</span>
            <span class="pan-value">{{ camera.camera_name }}</span>
          </div>
        }
      </div>
      <div class="space-y-1">
        @for (camera of locationCameras(); track camera.location_camera_id; let i = $index) {
          <div class="flex gap-2">
            <span class="font-semibold">Camera ID:</span>
            <span class="pan-value-tight">{{ camera.provider_camera_id }}</span>
          </div>
        }
      </div>
      <div class="flex items-center gap-2 -translate-y-2">
        <span class="font-semibold mr-2">Date:</span>
        <app-pan-button icon="chevron_left" ariaLabel="Previous day" (click)="onSnapshotDateChange(-1)" />
        <span class="pan-value">{{ pageDateStr() }}</span>
        <app-pan-button icon="chevron_right" ariaLabel="Next day" (click)="onSnapshotDateChange(1)" />
      </div>
    </div>
  </div>
</app-pan-card>

<app-pan-section-header title="Snapshot Schedule" class="mt-4 mb-4">
  <app-pan-help size="md" helpText="Schedules are applied going forward only."></app-pan-help>

  <div action class="flex gap-2 items-center">
    @if (!isEditScheduleMode()) {
      <app-pan-button size="md" color="info" (click)="toggleEditSchedule()" data-testid="edit-schedule-button"
        >Edit Schedule</app-pan-button
      >
    } @else {
      <app-pan-button size="md" color="warning" (click)="cancelEditSchedule()" data-testid="cancel-schedule-button">Cancel</app-pan-button>
      <app-pan-button size="md" color="info" (click)="setSchedule()" data-testid="set-schedule-button">Set Schedule</app-pan-button>
    }
  </div>
</app-pan-section-header>

<ng-template #addButtonTemplate>
  <app-pan-button color="info" icon="add" (click)="addSchedule()" data-testid="add-schedule-button" />
</ng-template>

<div class="mb-6 px-1">
  <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 items-start">
    @for (schedule of snapshotSchedules(); track $index; let i = $index) {
      <div class="flex flex-col w-full">
        <label class="text-xs font-normal mb-1">Time {{ i + 1 }}</label>
        <div class="flex gap-2 items-center">
          <input
            panInput
            type="time"
            [(ngModel)]="schedule.time"
            class="flex-1 h-[38px]"
            [disabled]="!isEditScheduleMode()"
            data-testid="schedule-time-input"
          />

          @if (isEditScheduleMode()) {
            @if (snapshotSchedules().length > 1) {
              <app-pan-button color="danger" icon="delete" (click)="removeSchedule(i)" data-testid="remove-schedule-button" />
            }

            @if (i === snapshotSchedules().length - 1 && snapshotSchedules().length < 3) {
              <ng-container *ngTemplateOutlet="addButtonTemplate"></ng-container>
            }
          }
        </div>
      </div>
    }

    @if (isEditScheduleMode() && snapshotSchedules().length === 0) {
      <div class="flex flex-col w-full">
        <label class="text-xs font-normal mb-1 invisible">Add</label>
        <div class="flex gap-2 items-center">
          <ng-container *ngTemplateOutlet="addButtonTemplate"></ng-container>
        </div>
      </div>
    }
  </div>
</div>

<app-pan-section-header title="Snapshots" class="mt-4 mb-4">
  <div action [class.hidden]="locationCameras().length === 0">
    <app-pan-button color="info" [matMenuTriggerFor]="requestPhotosMenu" data-testid="request-photos-dropdown">
      <div class="flex items-center gap-1">
        Request Photos
        <mat-icon class="text-white">keyboard_arrow_down</mat-icon>
      </div>
    </app-pan-button>

    <mat-menu #requestPhotosMenu="matMenu">
      <button mat-menu-item (click)="openCameraSnapshotPreview('live')" data-testid="request-now-menu-item">Request Now</button>
      <button mat-menu-item (click)="openCameraSnapshotPreview('selected')" data-testid="select-time-menu-item">Select Time</button>
    </mat-menu>
  </div>
</app-pan-section-header>

@if (isLoading()) {
  <app-pan-card>
    <app-pan-blank-slate>
      <app-pan-spinner size="xl"></app-pan-spinner>
      Loading snapshots...
    </app-pan-blank-slate>
  </app-pan-card>
} @else {
  @if (snapshotGroups().length === 0) {
    <app-pan-card>
      <app-pan-blank-slate>
        <mat-icon class="scale-[2] mb-4">photo_camera_off</mat-icon>
        No snapshots available for this location.
      </app-pan-blank-slate>
    </app-pan-card>
  } @else {
    <div class="mt-4 space-y-4">
      @for (group of snapshotGroups(); track group.requestDateTime; let snapIdx = $index) {
        <app-pan-card class="mb-4" data-testid="snapshot-group">
          <div class="p-6">
            <div class="flex flex-col items-center mb-6">
              <h4 class="text-lg font-bold">Snapshot #{{ snapIdx + 1 }}</h4>
              <p class="text-xs font-normal mt-0.5">
                {{ group.requestDateTime | date: "hh:mm:ss a" }} ({{ locationData()?.timezone ?? "US/Eastern" }})
              </p>
            </div>

            <div class="flex flex-col md:flex-row gap-6">
              <div class="grid gap-2 w-full" [ngClass]="group.images.length === 1 ? 'md:w-1/2 grid-cols-1' : 'md:w-3/5 grid-cols-2'">
                @for (image of group.images; track image.cameraName) {
                  @let imageRef = image.image$ | async;
                  @let isImageLoading = imageRef === null || imageRef?.isLoading;

                  <div class="bg-white border border-gray-300 rounded overflow-hidden flex flex-col h-full shadow-sm">
                    <div class="relative bg-neutral-100 overflow-hidden aspect-[16/9] flex items-center justify-center">
                      @if (image.snapshotError) {
                        <app-pan-blank-slate data-testid="snapshot-detail-camera-error">
                          <mat-icon class="scale-150">videocam_off</mat-icon>
                          <span class="text-sm">{{ cameraErrorMessage(image) }}</span>
                        </app-pan-blank-slate>
                      } @else if (isImageLoading) {
                        <app-pan-spinner></app-pan-spinner>
                      } @else if (imageRef?.value) {
                        <img
                          [alt]="image.cameraName"
                          class="w-full h-full object-cover cursor-pointer"
                          [src]="imageRef.value | blobToImage"
                          (click)="openImageModal(image, imageRef)"
                        />
                      } @else {
                        <mat-icon class="scale-[1.5]">image</mat-icon>
                      }
                    </div>

                    <div class="bg-gray-400 text-white px-3 py-1 flex items-center justify-between">
                      <span class="text-xs truncate">{{ image.cameraName }}</span>
                      @if (imageRef?.value) {
                        <mat-icon class="cursor-pointer text-sm" (click)="openImageModal(image, imageRef)">open_in_full</mat-icon>
                      }
                    </div>
                  </div>
                }
              </div>

              <div class="flex flex-col gap-3 w-full" [ngClass]="group.images.length === 1 ? 'md:w-1/2' : 'md:w-2/5'">
                <app-pan-form-control>
                  <app-pan-floating-label>Market Health Status</app-pan-floating-label>
                  <ng-select
                    [items]="snapshotStatusOptions"
                    bindLabel="label"
                    bindValue="value"
                    [clearable]="false"
                    placeholder="Select market health status..."
                    [(ngModel)]="group.status"
                    (change)="updateSnapshotRequest(group)"
                    data-testid="health-status-select"
                  ></ng-select>
                </app-pan-form-control>

                <app-pan-form-control class="flex-1 min-h-0">
                  <app-pan-floating-label>Comments</app-pan-floating-label>
                  <textarea
                    panTextarea
                    class="flex-1 w-full resize-y"
                    rows="6"
                    placeholder="What did you notice?"
                    [(ngModel)]="group.comments"
                    (change)="updateSnapshotRequest(group)"
                    data-testid="snapshot-comments-textarea"
                  ></textarea>
                </app-pan-form-control>
              </div>
            </div>
          </div>
        </app-pan-card>
      }
    </div>
  }
}
